import React, { useState } from "react"
import PropTypes from "prop-types"
import { Banner, Form, FormLayout, Modal, TextField } from "@shopify/polaris"
import { useTranslation } from "react-i18next"

/**
 * Centralized Nexthink configuration modal
 * Allows configuring Nexthink credentials once for the entire platform
 */
const NexthinkConfigModal = ({ isOpen, onClose, onSave }) => {
  const { t } = useTranslation()

  const [config, setConfig] = useState({
    nexthinkUrl: "",
    username: "",
    password: "",
  })

  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = (value, field) => {
    setConfig((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!config.nexthinkUrl) {
      newErrors.nexthinkUrl = t(
        "developer_portal:connectors.nexthink.config.errors.url_required"
      )
    } else if (!config.nexthinkUrl.startsWith("https://")) {
      newErrors.nexthinkUrl = t(
        "developer_portal:connectors.nexthink.config.errors.url_invalid"
      )
    }

    if (!config.username) {
      newErrors.username = t(
        "developer_portal:connectors.nexthink.config.errors.username_required"
      )
    }

    if (!config.password) {
      newErrors.password = t(
        "developer_portal:connectors.nexthink.config.errors.password_required"
      )
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    try {
      await onSave(config)
      setConfig({
        nexthinkUrl: "",
        username: "",
        password: "",
      })
    } catch (error) {
      console.error("Error saving Nexthink config:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setConfig({
      nexthinkUrl: "",
      username: "",
      password: "",
    })
    setErrors({})
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t("developer_portal:connectors.nexthink.config.title")}
      primaryAction={{
        content: t("developer_portal:connectors.nexthink.config.save"),
        onAction: handleSubmit,
        loading: isLoading,
      }}
      secondaryActions={[
        {
          content: t("common:cancel"),
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <Banner status="info">
          {t("developer_portal:connectors.nexthink.config.info")}
        </Banner>
        <Form onSubmit={handleSubmit}>
          <FormLayout>
            <TextField
              label={t(
                "developer_portal:connectors.nexthink.config.fields.url"
              )}
              value={config.nexthinkUrl}
              onChange={(value) => handleChange(value, "nexthinkUrl")}
              placeholder="https://your-instance.nexthink.com"
              error={errors.nexthinkUrl}
              autoComplete="off"
            />
            <TextField
              label={t(
                "developer_portal:connectors.nexthink.config.fields.username"
              )}
              value={config.username}
              onChange={(value) => handleChange(value, "username")}
              error={errors.username}
              autoComplete="username"
            />
            <TextField
              label={t(
                "developer_portal:connectors.nexthink.config.fields.password"
              )}
              type="password"
              value={config.password}
              onChange={(value) => handleChange(value, "password")}
              error={errors.password}
              autoComplete="current-password"
            />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  )
}

NexthinkConfigModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default NexthinkConfigModal

